/**
 * AdminTranslationStatus — Translation completeness dashboard
 * Shows per-collection, per-record translation status for admin oversight.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Globe, Check, AlertCircle, Copy, ChevronDown, ChevronUp, Loader2, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../../components/Toaster';
import api from '../../utils/api';


const COLLECTIONS = [
  { key: 'treatment_categories', label: 'Categories', icon: '🗂️' },
  { key: 'packages', label: 'Packages', icon: '📦' },
  { key: 'landing_content_blocks', label: 'CMS Blocks', icon: '📝' },
  { key: 'certifications', label: 'Certifications', icon: '🏅' },
];

const POLICY_BADGE = {
  translatable_required: { label: 'Required', cls: 'bg-red-50 text-red-600 border-red-200' },
  translatable_optional: { label: 'Optional', cls: 'bg-amber-50 text-amber-600 border-amber-200' },
  fallback_to_en: { label: 'Fallback', cls: 'bg-blue-50 text-blue-600 border-blue-200' },
  display_fallback_only: { label: 'Display FB', cls: 'bg-gray-50 text-gray-500 border-gray-200' },
  non_translatable: { label: 'N/A', cls: 'bg-gray-50 text-gray-400 border-gray-200' },
};

const STATUS_BADGE = {
  complete: { label: 'Complete', cls: 'bg-green-100 text-green-700' },
  partial: { label: 'Partial', cls: 'bg-amber-100 text-amber-700' },
  en_only: { label: 'EN only', cls: 'bg-blue-100 text-blue-700' },
  empty: { label: 'Empty', cls: 'bg-red-100 text-red-700' },
};

const CollectionPanel = ({ col }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [expandedRecord, setExpandedRecord] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = '';
      const res = await api.get(`/admin/i18n/completeness/${col.key}`, {
        headers: {},
      });
      const d = res.data;
      if (d.success) setData(d);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [col.key]);

  useEffect(() => { if (expanded && !data) fetchData(); }, [expanded, data, fetchData]);

  const handleCopyFromEn = async (recordId, field) => {
    try {
      const token = '';
      const res = await api.post(`/admin/i18n/copy-from-english`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', },
        body: JSON.stringify({ collection: col.key, record_id: recordId, field }),
      });
      const d = res.data;
      if (d.success) {
        toast({ title: 'Copied', description: `EN copied to ${d.filled_count} language(s) for "${field}"` });
        fetchData();
      } else {
        toast({ title: 'Error', description: d.error, variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Error', description: String(e), variant: 'destructive' });
    }
  };

  // Summary counts
  const summary = data ? {
    total: data.count,
    complete: data.records.filter(r => r.fully_translated).length,
    partial: data.records.filter(r => !r.fully_translated && r.overall_filled > 0).length,
    empty: data.records.filter(r => r.overall_filled === 0).length,
    fallbackSafe: data.records.filter(r => r.fallback_safe).length,
  } : null;

  return (
    <Card className="border shadow-sm" data-testid={`translation-panel-${col.key}`}>
      <CardHeader className="py-3 px-4 cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-lg">{col.icon}</span>
            <CardTitle className="text-sm font-semibold">{col.label}</CardTitle>
            {summary && (
              <div className="flex items-center gap-1.5 ml-2">
                {summary.complete > 0 && (
                  <Badge variant="outline" className="text-[9px] bg-green-50 text-green-600 border-green-200">
                    <Check className="w-2.5 h-2.5 mr-0.5" />{summary.complete} complete
                  </Badge>
                )}
                {summary.partial > 0 && (
                  <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-600 border-amber-200">
                    {summary.partial} partial
                  </Badge>
                )}
                <Badge variant="outline" className="text-[9px] bg-blue-50 text-blue-600 border-blue-200">
                  {summary.fallbackSafe}/{summary.total} fallback safe
                </Badge>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {loading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
            {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </div>
        </div>
      </CardHeader>

      {expanded && data && (
        <CardContent className="pt-0 pb-3 px-4">
          {/* Model field policies */}
          <div className="flex flex-wrap gap-2 mb-3 pb-2 border-b">
            {Object.entries(data.model_fields || {}).map(([f, p]) => {
              const info = POLICY_BADGE[p] || POLICY_BADGE.non_translatable;
              return (
                <span key={f} className={`text-[10px] px-2 py-0.5 rounded border ${info.cls}`}>
                  <strong>{f}</strong>: {info.label}
                </span>
              );
            })}
          </div>

          {/* Records */}
          <div className="space-y-1.5">
            {data.records.map(rec => {
              const isExpRec = expandedRecord === rec.id;
              return (
                <div key={rec.id} className="border rounded-lg">
                  <div
                    className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-gray-50"
                    onClick={() => setExpandedRecord(isExpRec ? null : rec.id)}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xs font-medium text-gray-800 truncate max-w-[200px]">{rec.label || rec.id}</span>
                      <span className="text-[10px] text-gray-400">{rec.overall_filled}/{rec.overall_total}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {!rec.all_required_have_en ? (
                        <Badge variant="outline" className="text-[9px] bg-red-50 text-red-600 border-red-200">
                          <AlertCircle className="w-2.5 h-2.5 mr-0.5" />Needs EN
                        </Badge>
                      ) : rec.fallback_safe ? (
                        <Badge variant="outline" className="text-[9px] bg-green-50 text-green-600 border-green-200">Safe</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-600 border-amber-200">
                          <AlertCircle className="w-2.5 h-2.5 mr-0.5" />Fallback risk
                        </Badge>
                      )}
                      {isExpRec ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    </div>
                  </div>

                  {isExpRec && (
                    <div className="px-3 pb-2 space-y-1.5 border-t bg-gray-50/50">
                      {Object.entries(rec.fields).map(([fieldName, fs]) => {
                        const si = STATUS_BADGE[fs.status] || STATUS_BADGE.empty;
                        return (
                          <div key={fieldName} className="flex items-center justify-between py-1">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium text-gray-600 w-32">{fieldName}</span>
                              <Badge className={`text-[9px] ${si.cls}`}>{si.label}</Badge>
                              <span className="text-[10px] text-gray-400">{fs.filled}/{fs.total}</span>
                              {fs.missing.length > 0 && (
                                <span className="text-[10px] text-gray-400">
                                  missing: {fs.missing.join(', ')}
                                </span>
                              )}
                            </div>
                            {fs.status !== 'complete' && fs.status !== 'empty' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 text-[10px] text-blue-500 hover:text-blue-700 px-2"
                                onClick={(e) => { e.stopPropagation(); handleCopyFromEn(rec.id, fieldName); }}
                                data-testid={`copy-en-${rec.id}-${fieldName}`}
                              >
                                <Copy className="w-3 h-3 mr-1" /> Copy EN
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default function AdminTranslationStatus() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="admin-translation-status">
      <Toaster />
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-button">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Globe className="w-5 h-5 text-blue-500" />
              Translation Status
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">
              Monitor and manage multilingual content completeness across all platform entities
            </p>
          </div>
        </div>

        {/* Quick Summary Bar */}
        <div className="grid grid-cols-4 gap-3 mb-4">
          <div className="bg-white rounded-lg border p-3 text-center">
            <Globe className="h-4 w-4 text-blue-400 mx-auto mb-1" />
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">Collections</p>
            <p className="text-lg font-bold text-gray-900">{COLLECTIONS.length}</p>
          </div>
          <div className="bg-white rounded-lg border p-3 text-center">
            <Check className="h-4 w-4 text-green-400 mx-auto mb-1" />
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">Status</p>
            <p className="text-xs font-medium text-green-600 mt-1">Launch Safe</p>
          </div>
          <div className="bg-white rounded-lg border p-3 text-center">
            <AlertCircle className="h-4 w-4 text-amber-400 mx-auto mb-1" />
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">Action Needed</p>
            <p className="text-xs font-medium text-amber-600 mt-1">Expand Below</p>
          </div>
          <div className="bg-white rounded-lg border p-3 text-center">
            <Copy className="h-4 w-4 text-blue-400 mx-auto mb-1" />
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">Quick Fix</p>
            <p className="text-xs font-medium text-blue-600 mt-1">"Copy EN" Button</p>
          </div>
        </div>

        {/* How to use */}
        <div className="mb-4 p-3 bg-white rounded-lg border border-slate-200 text-xs text-slate-600" data-testid="translation-how-to">
          <p className="font-semibold text-slate-700 mb-1">How to use this page:</p>
          <ol className="list-decimal list-inside space-y-0.5 text-[11px] text-slate-500">
            <li><strong>Click a collection</strong> to expand it and see individual records</li>
            <li><strong>Click a record</strong> to see per-field, per-language translation status</li>
            <li>Fields marked <Badge className="text-[8px] bg-red-100 text-red-700 px-1 py-0 inline">Empty</Badge> or <Badge className="text-[8px] bg-amber-100 text-amber-700 px-1 py-0 inline">Partial</Badge> need translations</li>
            <li>Use the <strong>"Copy EN"</strong> button to fill all languages with English text as a starting placeholder</li>
            <li>Items showing <Badge className="text-[8px] bg-red-50 text-red-600 border-red-200 px-1 py-0 inline">Needs EN</Badge> are missing English content — fix these first</li>
          </ol>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-2 mb-4 p-3 bg-white rounded-lg border text-[10px]">
          <span className="font-medium text-gray-600 mr-1">Status:</span>
          {Object.entries(STATUS_BADGE).map(([k, v]) => (
            <Badge key={k} className={`${v.cls} text-[9px]`}>{v.label}</Badge>
          ))}
          <span className="mx-2 text-gray-300">|</span>
          <span className="font-medium text-gray-600 mr-1">Policies:</span>
          {Object.entries(POLICY_BADGE).filter(([k]) => k !== 'non_translatable').map(([k, v]) => (
            <span key={k} className={`px-1.5 py-0.5 rounded border ${v.cls}`}>{v.label}</span>
          ))}
        </div>

        {/* Collection panels */}
        <div className="space-y-3">
          {COLLECTIONS.map(col => (
            <CollectionPanel key={col.key} col={col} />
          ))}
        </div>

        <div className="mt-6 p-3 bg-blue-50 rounded-lg border border-blue-200 text-xs text-blue-700">
          <strong>Launch readiness:</strong> All content is fallback-safe — missing translations display English.
          Use "Copy EN" to quickly fill placeholders, then replace with real translations later.
        </div>
      </div>
    </div>
  );
}
